import { rentalAgreementText } from '../../lib/rentals';

export interface RentalCheckoutTerm {
  name: string;
  quantity: number;
  rateLabel: string;
  interval: 'week' | 'month';
  /** This product's agreement. Blank uses the standard text. */
  agreement?: string | null;
}

/** One block when every rental shares the same wording; otherwise one per product. */
export function rentalAgreementBlocks(rentals: RentalCheckoutTerm[], businessName: string) {
  const rows = rentals.map((rental, index) => ({
    key: `${rental.name}-${index}`,
    title: rental.name,
    text: rentalAgreementText(rental.agreement, businessName),
  }));
  const unique = new Set(rows.map(row => row.text));
  if (unique.size <= 1) {
    return [{ key: 'agreement', title: null as string | null, text: rows[0]?.text || rentalAgreementText(null, businessName) }];
  }
  return rows.map(row => ({ ...row, title: row.title as string | null }));
}

interface RentalCartNoticeProps {
  rentals: RentalCheckoutTerm[];
  businessName: string;
  /** First-period amount, already formatted, e.g. "$14.99". */
  firstPeriodLabel: string;
  /** True when only the rental is charged now and the rest is invoiced later. */
  splitCharge: boolean;
  agreed: boolean;
  onAgreedChange: (agreed: boolean) => void;
  accent: string;
}

/**
 * Checkout rental box. The agreement renders here, in a fixed-height scroller,
 * and the customer checks the box before the card can be charged.
 */
export default function RentalCartNotice({
  rentals,
  businessName,
  firstPeriodLabel,
  splitCharge,
  agreed,
  onAgreedChange,
  accent,
}: RentalCartNoticeProps) {
  const periodWord = rentals.some(rental => rental.interval === 'week') ? 'period' : 'month';
  const blocks = rentalAgreementBlocks(rentals, businessName);

  return (
    <section className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
      <h3 className="text-[15px] font-bold text-gray-900 mb-2">Rental in your cart</h3>
      <div className="space-y-1.5 mb-2">
        {rentals.map((rental, index) => (
          <div key={`${rental.name}-${index}`} className="flex items-center justify-between text-[13px]">
            <span className="text-gray-800 font-medium">{rental.quantity} × {rental.name}</span>
            <span className="text-indigo-700 font-semibold">{rental.rateLabel}</span>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-gray-600 mb-2">
        The first {periodWord} ({firstPeriodLabel}) is charged {splitCharge ? 'now' : 'with your order'}, then billed automatically each period.
      </p>
      <div className="max-h-24 overflow-y-auto rounded-lg border border-indigo-100 bg-white/80 px-3 py-2">
        {blocks.map(block => (
          <div key={block.key} className="mb-2 last:mb-0">
            {block.title && (
              <p className="text-[12px] font-semibold text-gray-800 mb-1">{block.title}</p>
            )}
            <p className="text-[13px] text-gray-600 whitespace-pre-wrap leading-relaxed">{block.text}</p>
          </div>
        ))}
      </div>
      <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => onAgreedChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
          style={{ accentColor: accent }}
        />
        <span className="text-[13px] font-medium text-gray-800">I agree to the rental terms</span>
      </label>
    </section>
  );
}
